/* eslint-disable @typescript-eslint/no-explicit-any */
import config from 'src/config'
import { getUTMsForAnalytics } from 'src/utils/utms'
import { isInternalTraffic } from './internal'
import posthog from './posthog'

declare global {
  interface Window {
    dataLayer: any[]
    gtag: Gtag.Gtag
  }
}

// eslint-disable-next-line no-console
const dummy = () => console.info

export const EU_REGION = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'GB'
]

export type Gtag =
  | 'login'
  | 'sign_up'
  // Social/OAuth sign-in failure (Google / Facebook popup, Google One Tap).
  // Fires from the `.catch` of each provider flow with `method` + Firebase
  // error `code`. Diagnostic signal for in-app-browser / storage-partition
  // auth breakage — NOT a conversion, NEVER add to TIER_ROLLUP_MAP.
  | 'auth_error'
  // Login-funnel step events. Together with the existing `auth_modal_open` /
  // `auth_modal_abandoned` (entry) and `login` / `sign_up` (success), these
  // make the whole auth funnel measurable per method (phone/email/google/
  // facebook), per surface (modal vs /login page), and — via PostHog/GA4's
  // built-in device dimension — per mobile/desktop. All carry `surface`;
  // most carry `method`. Internal funnel/diagnostic signals — NEVER add any
  // of these to TIER_ROLLUP_MAP and never bid on them.
  | 'auth_method_select' // user picked a method (social button click, phone/email toggle) — `method`
  | 'auth_phone_submit' // valid phone submitted → OTP requested (method=phone)
  | 'auth_otp_sent' // Firebase confirmed SMS dispatch (signInWithPhoneNumber resolved)
  | 'auth_otp_submit' // user submitted a 6-digit code (confirm attempt)
  | 'auth_otp_resend' // user tapped resend / "more options" on the OTP screen
  | 'auth_email_continue' // email submitted at the prompt → existence lookup
  | 'auth_email_exists' // lookup result — `exists` (true=returning login, false=new register)
  | 'auth_password_submit' // password submitted on the returning-user login step
  | 'auth_register_submit' // register form submitted (createUserWithEmailAndPassword attempt)
  | 'auth_verification_sent' // verification email dispatched (post-register / post-unverified-login / resend)
  | 'auth_verification_completed' // email verified via /auth/action applyActionCode
  | 'auth_reset_requested' // password-reset email requested (forgot-password submit)
  | 'auth_reset_completed' // password reset confirmed via /auth/action confirmPasswordReset
  | 'email_typo_fix_accepted' // user tapped the "¿Quisiste decir …?" domain-typo hint — `surface` + suggested `domain`. Input-quality diagnostic, NEVER a conversion, NEVER in TIER_ROLLUP_MAP
  | 'email_typo_kept_as_typed' // login prompt's required typo choice: user kept the address as typed over the suggestion — `surface` + typed `domain`. False-positive gauge for the gate, NEVER a conversion, NEVER in TIER_ROLLUP_MAP
  | 'session_expired_bounce' // /login mounted with expired=1 — a previously-logged-in browser was bounced by a dead session (session-health signal, NEVER a conversion)
  | 'dashboard'
  | 'search'
  | 'search_completed'
  | 'view_item'
  | 'select_category'
  | 'add_to_cart'
  | 'broker_contact' // add to cart (Pro)
  | 'traditional_contact' // add to cart (Traditional lead-gen)
  | 'begin_checkout' // add to cart (Now)
  | 'contact_wa' // add to cart
  | 'chat_host' // add to cart
  | 'technical_report' // add to cart
  | 'on_demand' // add to cart (Almacenamiento a Domicilio lead form)
  | 'download_technical_report'
  | 'refund'
  | 'add_payment_info'
  | 'purchase'
  | 'share'
  | 'anfitrion_click'
  | 'host_customize'
  | 'host_contact_wa'
  | 'host_start'
  | 'generate_lead'
  | 'broker_lead' // SpotMe Pro form submission (funnel-specific campaign goal) — vertical-agnostic; prefer `broker_lead_storage` / `broker_lead_parking` for campaign optimization
  | 'broker_lead_storage' // Broker form submission on a STORAGE listing — vertical-scoped lead-creation event for Storage campaign goals
  | 'broker_lead_parking' // Broker form submission on a PARKING listing — vertical-scoped lead-creation event for Parking campaign goals
  | 'traditional_lead' // Traditional form submission — vertical-agnostic; NOT rolled up (use the segmented variants below for campaign optimization)
  | 'traditional_lead_storage' // Traditional lead submission on a STORAGE listing — vertical-scoped; rolls into storage_primary (replaces the bare traditional_lead rollup)
  | 'traditional_lead_parking' // Traditional lead submission on a PARKING listing — vertical-scoped; rolls into parking_primary
  // Soluciones Logísticas (3PL) — observed, never bid on; NOT in TIER_ROLLUP_MAP
  | 'logistics_view' // 3PL landing mount (/soluciones-logisticas) — funnel denominator, mirrors storage_on_demand_view. Params: `city` (only when geo/IP-resolved, never the fallback)
  | 'logistics_lead_start' // first interaction inside the 3PL hero form card (services / timeline / LeadForm) — mid-funnel, mirrors storage_on_demand_lead_start
  | 'logistics_lead' // 3PL hero form submission (/soluciones-logisticas)
  | 'logistics_contact_wa' // 3PL WhatsApp escape-hatch click (/soluciones-logisticas)
  | 'logistics_card_click' // B2B-pillar search-results cross-link card click (surface: 'grid' | 'empty')
  // Developers / investors (/anfitrion/desarrolladores) — observed, never bid on; NOT in TIER_ROLLUP_MAP, never a supply_* event
  | 'developer_view' // page mount — funnel denominator
  | 'developer_lead_start' // first focus inside the contact section
  | 'developer_lead' // contact form submitted. Params: `profile` (LAND | BUILDING | INVENTORY | INVESTOR | none)
  | 'logistics_service_toggle' // 3PL hero service pill toggled — params: `service` (slug), `selected`. Qualification signal, discovery-only
  // ── Directory listings (plan §4.1) — real, UNCLAIMED facilities published
  // below the search tier floor. ALL of these are OBSERVED-ONLY: never add one
  // to `TIER_ROLLUP_MAP`, never make one a Google Ads conversion. A directory
  // row is not monetized supply, so training a bidder on it would spend paid
  // budget driving renters to businesses we earn nothing from — the exact
  // dilution 4.2's guardrail exists to detect.
  | 'directory_impression' // a DirectoryCard entered the viewport — params: `surface`, `location_id`, `position`. Deduped once per (session, surface, location). MAP variant (2026-08-24): `surface: 'search_map'` fires once per session with `count` (markers rendered) and NO location_id — the dot layer's exposure denominator
  | 'directory_card_click' // click-through from a DirectoryCard to its ficha — params: `surface`, `location_id`, `position`
  | 'directory_reveal' // contact bundle revealed on a ficha — params: `location_id`, `channel`, `reveals_remaining`, `surface` (`ficha` | `search_zero_supply` | `search_weak_supply`). NOT a lead conversion: the renter contacts the facility directly, off-platform
  | 'directory_phone_open' // "Ver teléfono" opened the inline reveal form on a zero-supply city-guide card — params: `location_id`, `position`. Discovery only; the reveal itself is `directory_reveal` with `surface: search_zero_supply`
  | 'directory_reveal_capped' // the facility's monthly cap refused the reveal — params: `location_id`, `surface`. Unmet demand per facility = the claim-pitch ammo (plan §4.3)
  | 'directory_website_click' // revealed website opened — params: `location_id`
  | 'directory_map_open' // ficha pin opened in Google Maps (new tab) — params: `location_id`, `part` (map = the static map image, label = the link under it). Observed-only (2026-09-26)
  | 'directory_recovery_click' // renter left a ficha for verified SpotMe supply — params: `location_id`, `target` (verified_nearby | verified_nearby_capped | verified_nearby_disclaimer — the link under the disclaimer, 2026-09-26). The renter-recovery half of the directory bet; pair with `storage_on_demand_card_click` @ `surface: 'directory_ficha'` (2026-08-31)
  | 'directory_contact_click' // a revealed contact row tapped — params: `location_id`, `channel` (whatsapp | whatsapp_try | phone | email | directions). `whatsapp_try` = the phone-only guess (2026-08-28)
  | 'directory_claim_start' // "¿Este negocio es tuyo?" CTA — params: `location_id`, `surface`, `mode` (guest | auth — the 2026-08-28 wall flip). SUPPLY-side intent
  | 'directory_claim_linked' // a GUEST draft was adopted after sign-in — params: `location_id`. The wall was crossed; SUPPLY-side intent
  | 'directory_host_contact_click' // owner tapped a talk-to-us shortcut in the claim section instead of the form — params: `location_id`, `channel` (whatsapp | meeting). SUPPLY-side intent, observed-only (2026-08-28)
  | 'directory_claim_submitted' // claim sent to admin review — params: `location_id`, `strength` (VERIFIED_CODE | DOMAIN | DOCUMENT_ONLY | ASSERTED). SUPPLY-side intent
  | 'directory_claim_demand_shown' // the claim panel rendered demand evidence (§4.3 on-page pitch) — params: `location_id`, `nearby_shown`, `reveals_shown`, `capped_shown`. Deduped once per (session, location)
  | 'directory_removal_request' // facility asked for its ficha to come down (plan §3.6) — params: `location_id`, `delivered`. Health signal, never a conversion
  | 'host_location'
  | 'host_coordinates'
  | 'host_unit_details'
  | 'host_unit_description'
  | 'host_unit_access'
  | 'host_unit_price'
  | 'host_unit_photos'
  | 'host_unit_submit'
  | 'article_helpful'
  | 'help_search' // help-center search ran (debounced) — params: query_len, results
  | 'help_search_empty' // help-center search returned 0 results — params: query (≤60 chars)
  | 'help_related_click' // related-article click on a help/blog article — params: from_slug, to_slug
  // Google-review CTA click ("Dejar reseña en Google") — the measurable top of
  // the GBP review funnel (7 reviews all-time when instrumented 2026-08-13).
  // Params: `surface` ('write_review_modal' | 'closed_deal_details').
  // Diagnostic/reputation signal — NEVER a conversion, never in TIER_ROLLUP_MAP.
  | 'google_review_cta_click'
  // In-app pending-review banner (PendingReviewBanner on the dashboard homes).
  // Params: `surface` ('dashboard_banner'), `role` ('renter' | 'host').
  // shown → click is the banner's own funnel; review submission closes it in
  // the review-funnel KPI. Diagnostic — never in TIER_ROLLUP_MAP.
  | 'review_prompt_shown'
  | 'review_prompt_click'
  | 'conversion_event_add_to_cart'
  | 'valuation_feedback'
  | 'notify_me_submit' // Build B — renter left a phone on a sold-out/paused listing (demand capture → DemandWatch → supply-arrival re-touch). Intent, NOT a conversion; never add to TIER_ROLLUP_MAP.
  | 'cookie_consent' // action: shown | accept | reject | save_preferences | dismiss — `CookieConsentAction` in components/Cookies/consentBanner.ts
  // Team-member invites (supply-side; NOT rolled up into host_* — see TIER_ROLLUP_MAP)
  | 'team_invite_sent' // admin sent an invite from the dashboard panel
  | 'team_invite_viewed' // accept landing page rendered an invite preview
  | 'team_invite_accept_start' // invitee clicked the accept/join CTA
  | 'team_invite_accepted' // invite redeemed → joined the team
  | 'ab_test_exposure'
  | 'ab_test_conversion'
  | 'complete_kyc'
  // Segmented conversion events — campaign-specific Google Ads goals
  | 'storage_purchase'
  | 'storage_complete_kyc'
  | 'parking_monthly_purchase'
  | 'parking_monthly_complete_kyc'
  | 'parking_b2b_purchase'
  | 'parking_b2b_complete_kyc'
  | 'parking_b2b_qualified_lead'
  | 'parking_b2b_form_submit'
  | 'storage_qualified_lead'
  | 'qualified_whatsapp_lead'
  | 'listing_whatsapp_click'
  // Segmented add-to-cart events — vertical and vertical+dealtype variants
  // emitted alongside the bare add_to_cart sub-events so Google Ads (and Meta)
  // can configure campaign-specific Secondary goals per vertical/dealtype.
  // See spotmeEvents.addToCart for the fanout logic.
  | 'contact_wa_storage'
  | 'contact_wa_storage_now'
  | 'contact_wa_storage_broker'
  | 'contact_wa_storage_traditional'
  | 'contact_wa_parking'
  | 'contact_wa_parking_now'
  | 'contact_wa_parking_broker'
  | 'begin_checkout_storage'
  | 'begin_checkout_parking'
  | 'broker_contact_storage'
  | 'broker_contact_parking'
  | 'traditional_contact_storage'
  | 'traditional_contact_parking' // Traditional WA/lead-contact on a PARKING listing — vertical-scoped; rolls into parking_secondary
  | 'chat_host_storage'
  | 'chat_host_parking'
  | 'hero_cta_click'
  | 'final_cta_click'
  | 'calculator_link_click'
  | 'estimator_type_change'
  | 'estimator_cta_click'
  | 'program_selector_click'
  | 'whatsapp_cta_click'
  | 'host_cta_click'
  | 'calculate_step'
  // Public calculator v2 (spec 2026-10-08 §5) — all carry `arm` ('A'|'B') and `surface: 'calculator'`. Never join TIER_ROLLUP_MAP.
  | 'calc_entry_view'
  | 'calc_type_select'
  | 'calc_size_select'
  | 'calc_quantity_select'
  | 'calc_location_method'
  | 'calc_number_view'
  | 'calc_preview_edit'
  | 'calc_photo_upload_done'
  | 'calc_suggestion'
  | 'calc_preview_view'
  | 'calc_save_lead'
  | 'calc_claim_start'
  | 'calc_claimed'
  | 'section_view'
  // Search-results / map events — session-deduped exploration signals
  | 'search_result_click' // user clicks through to a listing from search surface
  | 'search_map_interaction' // user explores map (open marker, zoom, pan — pan session-deduped since 2026-07-16, see searchEvents.trackMapInteraction)
  | 'search_card_carousel_advance' // user advances photo carousel in search card
  | 'search_result_view' // list card entered the viewport (≥50%), once/session/card
  // WhatsApp pill on a Traditional/Broker search-grid card (2026-10-16,
  // origination levers chunk A). Params: `surface` ('search_card'),
  // `deal_type`, `location_id`, `configuration_id`, `position`, `zone`. Fires
  // on EVERY tap (the contact_wa / *_contact conversion stack beside it is
  // session-deduped). Discovery — NEVER add to TIER_ROLLUP_MAP.
  | 'search_card_whatsapp_click'
  // Search filter/sort UI — discovery signals, NEVER roll up. `select_category`
  // (above) fires on every filter/sort APPLY with `filter` (size|price|sort|distance),
  // `value`, and since 2026-07-16 `surface` (quick_filters | mobile_drawer |
  // desktop_modal). `filters_open` is the missing denominator: the full
  // filter drawer/modal became visible. Params: `surface` (mobile_drawer |
  // desktop_modal). Answers "do mobile users ever open the drawer" —
  // select_category alone can't separate never-opened from opened-and-bailed.
  | 'filters_open'
  // Nav SearchBar (WHERE/WHAT pill, desktop + mobile) engagement funnel.
  // `search_box_start` fires once per mount on first engagement (first
  // keystroke or first location select) — the denominator for
  // search_box_start → search_suggestion_select → search → search_completed.
  // `search_suggestion_select` fires when an autocomplete option is picked.
  // Params: `suggestion_type` (place | city_preset | near_me), `description`.
  | 'search_box_start'
  | 'search_suggestion_select'
  // /begin-search space-type picker (homepage hero routes here before /search).
  // The page itself was fully silent until 2026-07-16 — the space-type choice
  // is the highest-signal intent step between hero submit (`search`) and
  // results (`search_completed`). Anchor funnels on $pageview
  // pathname=/begin-search (no redundant _view event). Discovery — never roll up.
  | 'begin_search_select' // tile toggled — params: `space_type` (SpaceType), `selected` (boolean)
  | 'begin_search_continue' // Continuar → /search — params: `space_types` (csv), `space_type_count`, `has_location`
  // Zero-results recovery — WhatsApp escape hatch on the <CaptureLead> stack
  // (SearchManager). Fires when a dead-end / sparse searcher taps the green
  // "Escríbenos por WhatsApp" pill instead of filling the lead form. Distinct
  // from the listing-level `contact_wa` (reserved for listing intent) — this
  // one belongs to the zero-results recovery definition (FUNNELS.md F1 +
  // docs/marketing/zero-results-recovery.md), counted in recovery insight
  // SihInJwm. Discovery/recovery signal — NEVER add to TIER_ROLLUP_MAP.
  // Params: `zero_results`, `unserved_area`, `location_name`, `has_city_hint`.
  | 'search_empty_wa_click'
  // Listing page "Habla con un experto" WhatsApp button (Details/ContactUs,
  // the help band near the page bottom). Untracked until 2026-10-06. Discovery
  // signal, NOT listing purchase intent — NEVER add to TIER_ROLLUP_MAP.
  // Params: `has_session_token`, `has_gclid`.
  | 'listing_help_wa_click'
  // Almacenamiento a Domicilio funnel — storage-focused, one event per
  // funnel stage so each can be wired as a Google Ads conversion goal /
  // GA4 key event independently. Fire alongside the existing
  // `add_to_cart` + `on_demand` sub-event for the marketing-report
  // pipeline; these are the campaign-friendly equivalents.
  | 'storage_on_demand_view' // page mount on /almacenamiento-a-domicilio[/{city}]
  | 'storage_on_demand_lead_start' // first form-field interaction (mid-funnel)
  | 'storage_on_demand_lead' // form submit success (PRIMARY conversion)
  | 'storage_on_demand_whatsapp' // any WhatsApp CTA on the on-demand surface
  | 'storage_on_demand_card_view' // search cross-link card enters viewport (IO-gated, session-deduped; discovery denominator — NOT a conversion, never roll up)
  | 'storage_on_demand_card_click' // search cross-link card tap/click (discovery numerator — NOT a conversion, never roll up)
  // Supply-side (host) conversion events. Strictly separate from the
  // demand-side `generate_lead` / `parking_b2b_*` / `traditional_lead` /
  // `storage_*` family — supply pages MUST NOT fire those, demand pages
  // MUST NOT fire these. See spotmeEvents.supplyLead for the fanout.
  | 'supply_view' // first /anfitrion/* landing per session
  | 'supply_lead_start' // first form-field interaction on a supply form
  | 'supply_lead' // PRIMARY supply conversion — bucket event for all funnels
  // Per-funnel supply variants. Marketing optimizes Google Ads / Bing
  // campaigns on these for clean per-funnel conversion goals.
  | 'supply_lead_individual' // /anfitrion main page CTAs ("Subir mi espacio")
  | 'supply_lead_business' // /anfitrion/negocio (B2B host signup)
  | 'supply_lead_traditional' // /anfitrion/tradicional (Anexo C host signup)
  | 'supply_lead_calculate' // /anfitrion/valua-tu-propiedad (calculator submit)
  | 'supply_lead_broker' // /anfitrion/broker (broker program signup)
  // Soluciones Logísticas (3PL) — observed, never bid on; NOT in TIER_ROLLUP_MAP
  | 'supply_lead_logistics' // /anfitrion/soluciones-logisticas (3PL operator/host supply)
  | 'supply_lead_invite' // /anfitrion/invita (referral signup)
  | 'supply_lead_add_post' // AddPostFlow Success step — actually published a listing (deepest supply signal)
  | 'supply_listing_posted' // bare event for AddPostFlow Success step (no funnel fan-out — terminal supply conversion)
  // Tier-rollup events — fired automatically by `fireSegmentedEvent` and
  // `fireOnDemandEvent` whenever a primary/secondary tier event fires. The
  // mapping is `TIER_ROLLUP_MAP` in `spotmeEvents.ts`. Lets Google Ads campaign
  // goals optimize on 4 events (instead of importing 10+ GA4 Key Events per
  // campaign) — Storage campaign primary = `storage_primary`, secondary =
  // `storage_secondary`, etc. See `docs/marketing/analytics-reference.md`
  // §"Tier rollup" for the strategy + per-event mapping. The rollup only fires
  // to GA4 + GTM dataLayer; Meta / Bing / LinkedIn keep their existing
  // funnel-specific custom events untouched.
  | 'storage_primary' // Monthly Now + Traditional + OnDemand storage PRIMARY (traditional_lead_storage, storage_purchase, storage_on_demand_lead, generate_lead_preauth_storage). Excludes broker bodega/nave — different unit economics. NOTE: bare traditional_lead is NO LONGER rolled up — its storage coverage moved to the vertical-scoped traditional_lead_storage
  | 'storage_secondary' // Monthly Now + Traditional + OnDemand storage SECONDARY (traditional_contact_storage, begin_checkout_storage, begin_checkout_preauth_storage, storage_on_demand_whatsapp, storage_complete_kyc, contact_wa_storage_now, contact_wa_storage_traditional). Excludes broker
  | 'parking_primary' // any parking PRIMARY-tier event (parking_b2b_form_submit, parking_b2b_qualified_lead, parking_monthly_purchase, parking_b2b_purchase, broker_lead_parking, traditional_lead_parking, generate_lead_preauth_parking — fleet pill + preauth parking are on-platform B2C/B2B signal)
  | 'parking_secondary' // any parking SECONDARY-tier event (begin_checkout_parking, begin_checkout_preauth_parking, contact_wa_parking_now, contact_wa_parking_broker, traditional_contact_parking, parking_b2b_complete_kyc, parking_monthly_complete_kyc, broker_contact_parking)
  | 'host_primary' // any host (supply-side) PRIMARY-tier event (supply_lead bucket — any of the 6 funnels, supply_listing_posted)
  | 'host_secondary' // any host (supply-side) SECONDARY-tier event (supply_view, supply_lead_start)
  // Pre-auth /reservar funnel — splits the unauthed Monthly checkout flow
  // from the legacy `begin_checkout` → auth-modal flow so PostHog and GA4
  // funnels can measure both paths cleanly. `phone_submit_preauth` is the
  // lead-capture event (fires alongside the synchronous sendCustomerInfoEmail
  // mutation in the screen); `otp_verified_preauth` is the conversion
  // (account created, user authed). See docs/plans/2026-05-19-001-feat-
  // preauth-reservar-plan.md for the full funnel definition.
  | 'begin_checkout_preauth' // /reservar page mount for an unauthed visitor
  | 'phone_submit_preauth' // user tapped Confirmar with a phone number (LEAD captured here regardless of OTP outcome)
  | 'otp_sent_preauth' // Firebase confirmed SMS dispatch
  | 'otp_verified_preauth' // 6-digit code accepted — Firebase Phone Auth account created
  | 'preauth_to_checkout' // user landed on /checkout/create after OTP success (terminal /reservar conversion)
  // Vertical-segmented preauth variants — fire alongside the bare events
  // above so Google Ads can optimize per-pillar campaigns on the preauth
  // funnel without conflating storage and parking Monthly intent. Server-
  // side gate at /reservar locks dealType=Monthly + locationType to
  // SelfStorageUnit | Parking, so each event always belongs cleanly to
  // exactly one rollup. See TIER_ROLLUP_MAP in spotmeEvents.ts.
  | 'begin_checkout_preauth_storage' // /reservar mount for a self-storage Monthly listing → storage_secondary
  | 'begin_checkout_preauth_parking' // /reservar mount for a parking Monthly listing → parking_secondary
  | 'generate_lead_preauth_storage' // phone submit on a self-storage Monthly listing → storage_primary
  | 'generate_lead_preauth_parking' // phone submit on a parking Monthly listing → parking_primary
  // Size-guide engagement — fired by the in-listing StorageSizeModal
  // (Details page + SizeSection mounts) and by the standalone
  // /guia-de-tamanos page (SizeAccordion). Modal events carry a `surface`
  // breakdown ('listing_detail' | 'listing_size_section') so we can tell
  // which mount drives interactions; page events have no surface (single
  // mount). PRIMARY discovery signal — NOT a conversion, NEVER add to
  // TIER_ROLLUP_MAP. Used to measure size-guide reach + cross-link
  // efficiency to /reservar (size_modal_cta_click cta='search_by_size').
  | 'size_modal_open' // modal transitioned to open
  | 'size_modal_category_change' // user clicked a category tab in the modal
  | 'size_modal_size_select' // user tapped a size tile in the modal
  | 'size_modal_cta_click' // modal CTA — `cta` param: 'search_by_size' | 'open_size_guide'
  | 'size_guide_category_change' // user clicked a category tab on /guia-de-tamanos
  | 'size_guide_size_select' // user tapped a size row on /guia-de-tamanos
  | 'size_guide_cta_click' // /guia-de-tamanos CTA — `cta` param: 'search_by_size'
  // FAQ engagement — fires once per accordion item expansion (NOT on collapse,
  // would double-count). `surface` param identifies the page (seguro / disputas
  // / reembolso / anfitrion / flotillas / on-demand / size-guide / landing /
  // pillar-city / personalized / invite / negocio / tradicional). Discovery
  // signal — NEVER add to TIER_ROLLUP_MAP.
  | 'faq_expand' // user opened an accordion item — params: `surface`, `index`
  // Locale + currency preference switches. Until these existed there was NO
  // signal for whether anyone uses the ES/EN or MXN/USD toggles — the `/en`
  // pathname prefix on `$pageview` was the only proxy, and it cannot separate
  // a deliberate switch from a direct/bookmarked landing. Both are discovery
  // signals — NEVER add to TIER_ROLLUP_MAP.
  | 'locale_switch' // ES/EN toggle used — params: `from`, `to`, `surface`
  | 'currency_switch' // MXN/USD toggle used — params: `from`, `to`, `surface`
  // Plans-section CTA — distinct from hero/final CTAs so we can measure
  // mid-page conversion intent on plan-comparison surfaces (/seguro plans
  // section, future /precios, etc.). `page_type` matches the existing
  // hero_cta_click / final_cta_click convention.
  | 'plans_cta_click'
  // Listing detail interactions — discovery + safety surfaces beyond the
  // already-instrumented StorageSizeModal. All carry `listing_id`. NEVER
  // add to TIER_ROLLUP_MAP — these are engagement / abuse signals, not
  // conversions. See `Images.tsx`, `ReviewsModal.tsx`, `ReportLocationModal.tsx`.
  | 'listing_gallery_lightbox_open' // user opened the full-screen PhotoSwipe lightbox — params: `listing_id`, `photo_index`, `photo_count`, `source` (desktop | mobile)
  | 'listing_gallery_lightbox_close' // lightbox dismissed — ONE per open, carrying how far the user actually got: params: `listing_id`, `photo_count`, `photos_seen`, `deepest_index`, `source`. This is the depth metric; `..._open` only says they started. Fired on close rather than per-slide so the volume is one event per session-open, not one per swipe.
  | 'listing_gallery_carousel_advance' // mobile carousel scrolled to a new photo — params: `listing_id`, `to_index`
  | 'listing_gallery_grid_more_click' // desktop "Ver N fotos" badge click — params: `listing_id`, `photo_count`
  | 'reviews_modal_open' // host reviews modal opened — params: `listing_id`, `host_id`
  | 'reviews_modal_load_more' // load-more inside reviews modal — params: `host_id`, `page`
  | 'report_location_open' // report-listing modal opened — params: `listing_id`
  | 'report_location_submit' // report submitted — params: `listing_id`
  // Home + nav discovery — never roll up. Help locate which entry surfaces
  // route users into the funnel beyond the hero search bar.
  | 'home_city_tile_click' // user tapped a city chip on `/` — params: `city`, `pillar`, `is_closest`
  | 'nav_drawer_open' // mobile nav drawer became visible — params: `path` (current pathname)
  | 'nav_megamenu_open' // a desktop mega-menu panel opened — params: `menu` (storage | offer | resources), `via` (hover | click)
  | 'nav_link_click' // user tapped a global-nav link — params: `url`, `surface` (mobile_drawer | desktop_megamenu | footer)
  // Home mid-page sections — every CTA below the hero on `/`. One event;
  // `section` slices it (verticals | logistics_callout | steps | last_search)
  // and `cta` names the button within the section (primary | secondary |
  // whatsapp_specialist | view_services | search | resume | dismiss).
  // Discovery — NEVER add to TIER_ROLLUP_MAP.
  | 'home_section_cta_click'
  // Recommended/similar listing carousels. `surface` (home_recommended |
  // pillar_recommended | listing_similar) + `listing_id`. The search-results
  // grid keeps its own `search_result_click` — do not merge. Discovery —
  // NEVER roll up.
  | 'recommended_card_click'
  | 'recommended_view_more_click' // "Ver más" link beside the recommended carousel — params: `surface`
  | 'recommended_pillar_toggle' // storage ⇄ parking switch on the home rail — params: `surface`, `pillar` (the pillar switched TO)
  // Pillar-page cross-sell bands (CrossLinkOnDemand / CrossLinkLogistics).
  // Params: `to` (on_demand | logistics), `from_pillar`, `city` (only when
  // the band resolved a covered city). Discovery — NEVER roll up.
  | 'pillar_crosslink_click'
  // Every "publicar espacio"-shaped entry point into the AddPostFlow wizard,
  // instrumented together (2026-08) so the never-opened-the-wizard cohort
  // becomes measurable for the first time. `surface` is REQUIRED — see
  // `CreateListingCtaSurface` below and `fireCreateListingCtaClick`. Feeds
  // the KYC→wizard handoff funnel; discovery signal — NEVER add to
  // TIER_ROLLUP_MAP.
  | 'create_listing_cta_click'

// Surfaces for `create_listing_cta_click` — one entry per real entry point,
// each on its own line so the funnel reads unambiguously. Never pass a
// literal inline at the call site; always go through `fireCreateListingCtaClick`.
export type CreateListingCtaSurface =
  | 'kyc_success' // post-KYC continuation into the wizard — only fires when the KYC redirect target is the create-listing flow (src/components/UserConfirmation/steps/EmailSentStep.tsx)
  | 'dashboard_header' // persistent header "Agregar alojamiento" button (pages/dashboard/host/listings/index.tsx)
  | 'dashboard_zero_state' // host-has-zero-listings block that replaces the legacy <EmptyState> (src/components/Listings/ZeroListingsState.tsx)
  | 'listings_empty_tab' // surviving per-tab <EmptyState> CTA — a tab with 0 while other tabs have listings (src/components/Listings/index.tsx)
  | 'valuation_result' // "publica tu espacio" CTA on /anfitrion/valua-tu-propiedad/[id] (src/screens/Calculate/Result.tsx)

const getGtag: () => Gtag.Gtag = () =>
  typeof window !== 'undefined' && typeof window.gtag === 'function'
    ? (window.gtag ?? dummy)
    : dummy

const getDataLayer = (): any[] =>
  typeof window !== 'undefined' ? (window.dataLayer ?? []) : []

const setUser = (id: string) => {
  if (isInternalTraffic()) return
  const gtag = getGtag()
  const dataLayer = getDataLayer()
  dataLayer.push({ user_id: id })
  gtag('config', config.pixels.ga, { user_id: id })
  // PostHog parity — every GA4 user_id maps to a PostHog person. The
  // richer identify-with-email-phone-name path lives in
  // `common.tsx identifyUser` and runs alongside this whenever user
  // contact info is available.
  posthog.identifyUser({ id })
}

const pageview = (url: URL): void => {
  if (isInternalTraffic()) return
  const gtag = getGtag()
  gtag('config', config.pixels.ga, { page_path: url })
  // PostHog parity — every GA4 pageview also fires a PostHog
  // `$pageview` so SPA route changes show up in PostHog session
  // recordings, funnels, and path analyses.
  posthog.pageview(url)
}

const event = (action: Gtag, params?: any): void => {
  if (isInternalTraffic()) return
  const gtag = getGtag()
  const utmParams = getUTMsForAnalytics()

  const enrichedParams = { ...utmParams, ...params }

  gtag('event', action, enrichedParams)

  // Every primitive param, uncapped — PostHog has no property-count limit.
  // (Vercel Analytics, removed 2026-11-04 as a paid duplicate of PostHog,
  // capped at 16; feeding PostHog that capped object silently truncated the
  // TAIL of every wide event — `view_item` landed `sqm` in 0/623 rows on
  // 2026-09-11.) Non-primitives are dropped on purpose — GA4's nested
  // `items[]` / Meta's `contents[]` do not flatten into a usable PostHog
  // property, and that is what the flat `config_id` exists for.
  const isPrimitive = (v: unknown): v is string | number | boolean =>
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'

  const posthogParams: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries({ ...params, ...utmParams })) {
    if (isPrimitive(v)) posthogParams[k] = v
  }

  // Mirror to PostHog so all GA4-style conversion events show up in
  // PostHog dashboards / cohorts without callers having to fire twice.
  // PostHog is queued internally until the dynamic-imported library
  // finishes loading — safe to fire from anywhere.
  posthog.event(action, posthogParams)
}

const ga = { pageview, event, getDataLayer, getGtag, setUser }

/**
 * Fires `create_listing_cta_click` with a REQUIRED, type-checked `surface`
 * (see `CreateListingCtaSurface`). Centralizing the fire here — instead of
 * letting each of the 5 call sites build the params object inline — makes
 * it impossible to ship a call that defaults or typos the surface literal.
 */
export const fireCreateListingCtaClick = (
  surface: CreateListingCtaSurface
): void => event('create_listing_cta_click', { surface })

export default ga
